import { Component } from 'react';

import Dropdown from '../../../components/ui/dropdown/dropdown';
import { IOC_TYPES } from '../../../context/ioc.types';
import { lazyInject } from '../../../context/module';
import { FileUploadRequestMetadata } from '../../../models/file-upload-request-metadata';
import { FormControl, FormGroup } from '../../../models/forms';
import { LightHttp } from '../../../services/light-http.service';
import { ProcessorResolverService } from '../../../services/processor-resolver.service';
import { BrowserUtils } from '../../../ui-utils/browser-utils';
import { StringUtils } from '../../../utils/string-utils';
import styles from './base64-encode.component.module.scss';
import sharedStyles from '../single-page.module.scss';
import { ProcessorMetadata } from '../../../types/types';
import { ProcessorMetadataUtils } from '../../../utils/processor-metadata.utils';
import { FullscreenHost } from '../fullscreen-host.component';

export class Base64EncodeComponent extends Component {
    fileUploadRequestMetadata: FileUploadRequestMetadata;
    processEndpoint;
    ready = false;
    availableEncodingList: {
        id: string;
        text: string;
        item: string;
        selected: boolean;
    }[];
    encoding = 'UTF-8';
    lineWidth = '-1';
    linebreak = 'linux';
    form = new FormGroup({
        textinput: new FormControl(''),
        reversetextinput: new FormControl(''),
        lineSeperator: new FormControl('linux'),
        lineWidth: new FormControl(false),
    });

    @lazyInject(ProcessorResolverService) private resolverService: ProcessorResolverService;
    @lazyInject(LightHttp) private httpClinet: LightHttp;

    constructor(props) {
        super(props);
        if (BrowserUtils.hasWindow()) {
            this.resolverService.generateFileUploadRequestMetadataForGenericDataProcessing()
                .then(response => {
                    this.fileUploadRequestMetadata = response;
                    this.processEndpoint = ProcessorMetadataUtils.getProcessorEndpoint(this.fileUploadRequestMetadata.processorMetadataList[0])
                        + '/generic';
                    this.ready = true;
                });
        }
        this.availableEncodingList = this.getEncodingFormatSelectItems();
    }

    componentDidMount() {
        this.form.get('lineSeperator').valueChanges.subscribe(
            (data) => {
                this.linebreak = data;
                this.updateOutput(this.form.get('textinput').value);
            });
        this.form.get('lineWidth').valueChanges.subscribe(
            (data) => {
                if (data) {
                    this.lineWidth = '76';
                } else {
                    this.lineWidth = '-1';
                }
                this.updateOutput(this.form.get('textinput').value);
            });
        this.form.get('textinput').valueChanges.subscribe(
            () => {
                this.updateOutput(this.form.get('textinput').value);
            },
            (err) => console.error(err),
        );
    }

    private updateOutput(data: string) {
        // The backend round-trip was racing against an async endpoint resolution
        // that often left `processEndpoint` undefined — encode/decode silently
        // failed with a fetch to literal "undefined". Browser-native encoding
        // works for the UTF-8 default; non-UTF-8 encodings (EBCDIC etc.) fall
        // back to the server path if the endpoint is available.
        const encoded = this.encodeNative(data || '');
        if (encoded !== null) {
            this.form.get('reversetextinput').setValue(encoded, { noEmit: true });
            this.forceUpdate();
            return;
        }
        if (!this.processEndpoint) {
            // Server fallback not ready and native couldn't handle the encoding.
            this.form.get('reversetextinput').setValue(
                `// Encoding ${this.encoding} requires server processing; please retry in a moment.`,
                { noEmit: true });
            this.forceUpdate();
            return;
        }
        const qdata = btoa(JSON.stringify({
            processType: 'encode',
            encoding: this.encoding,
            lineWidth: this.lineWidth,
            linebreak: this.linebreak,
        }));
        this.httpClinet.post<{ data: string }>(
            this.processEndpoint + '?qdata=' + encodeURIComponent(qdata),
            data,
        ).subscribe({
            next: response => {
                this.form.get('reversetextinput').setValue(response.data, { noEmit: true });
                this.forceUpdate();
            },
            error: (err) => { console.log(err); },
        });
    }

    private encodeNative(data: string): string | null {
        if (this.encoding !== 'UTF-8') return null;
        const bytes = new TextEncoder().encode(data);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        let encoded = btoa(binary);
        const width = parseInt(this.lineWidth, 10);
        if (width > 0) {
            const sep = this.linebreak === 'windows' ? '\r\n' : '\n';
            encoded = encoded.match(new RegExp(`.{1,${width}}`, 'g'))?.join(sep) || encoded;
        }
        return encoded;
    }

    encodingSelected(value: any) {
        this.encoding = value.id;
        this.updateOutput(this.form.get('textinput').value);
    }

    getEncodingFormatSelectItems() {
        const selecteItems = StringUtils.supportedTextEncoding().map(item => {
            return {
                id: item[1],
                text: item[0],
                item: item[1],
                selected: false,
            };
        });
        selecteItems[0].selected = true;
        return selecteItems;
    }

    setRadioInputValue(ctrl: FormControl, value: any) {
        ctrl.setValue(value);
    }

    render() {
        return (<FullscreenHost><div className="row">
            <div className="col-12">
                <label className={sharedStyles.label}>Input</label>
                <textarea className={sharedStyles.textarea}
                    placeholder="Enter text to base64 encode..."
                    value={this.form.controls['textinput'].value}
                    onInput={(e: any) => this.form.controls['textinput'].setValue(e.target.value)} rows={6}></textarea>
            </div>

            <div className="col-12">
                <div className={sharedStyles.controls}>
                    <div className={sharedStyles.radioGroup}>
                        <label className={sharedStyles.label} style={{ marginBottom: 0, whiteSpace: "nowrap" }}>Destination charset:</label>
                        <div style={{ minWidth: '200px' }}>
                            <Dropdown
                                values={this.availableEncodingList}
                                label={'Presets'}
                                valueFormatter={(v: any) => {
                                    return v && v.text;
                                }}
                                onSelect={e => this.encodingSelected(e)}
                                initialValue={this.availableEncodingList.find(v => v.selected)}
                                hasTooltip={(v) => false}
                                getTooltip={(v) => v.text}
                                shownFn={(v) => true}
                                emitInitialValueOnInit={true}
                            >
                            </Dropdown>
                        </div>
                    </div>

                    <div className={sharedStyles.radioGroup}>
                        <label className={sharedStyles.label} style={{ marginBottom: 0 }}>Newline:</label>
                        <label className={sharedStyles.radioLabel}>
                            <input type="radio"
                                name="lineSeperator"
                                onChange={(e: any) => this.setRadioInputValue(this.form.controls['lineSeperator'], e.target.value)}
                                value="linux"
                                checked={this.form.controls['lineSeperator'].value === 'linux'} />
                            LF (Linux)
                        </label>
                        <label className={sharedStyles.radioLabel}>
                            <input type="radio"
                                name="lineSeperator"
                                onChange={(e: any) => this.setRadioInputValue(this.form.controls['lineSeperator'], e.target.value)}
                                value="windows"
                                checked={this.form.controls['lineSeperator'].value === 'windows'} />
                            CRLF (Windows)
                        </label>
                    </div>

                    <div className={sharedStyles.radioGroup}>
                        <label className={sharedStyles.radioLabel}>
                            <input type="checkbox"
                                id="customCheck1"
                                onChange={(e: any) => this.form.controls['lineWidth'].setValue(e.target.checked)}
                            />
                            Split lines (76 chars)
                        </label>
                    </div>
                </div>
            </div>

            <div className="col-12">
                <label className={sharedStyles.label}>Output</label>
                <textarea className={sharedStyles.textarea}
                    placeholder="Result will appear here..."
                    value={this.form.controls['reversetextinput'].value}
                    readOnly
                    rows={6}></textarea>
            </div>
        </div></FullscreenHost>);}
}
